import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Modal, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { plans as plansApi, usagefees } from '../../api/fee'
import type { Plan, UsageFee } from '../../api/types'
import { Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { CURRENCIES, usageFeePriceText } from '../../lib/catalogue'
import { formatNumberN } from '../../lib/format'
import { useMoneyAction } from '../../lib/useMoneyAction'

const DECIMAL = /^\d+(?:\.\d+)?$/

/**
 * Port of `UsagefeeListComponent` (`projects/:projectId/usagefees[/:usagefeeId]`):
 * metered fees, a cost calculator in the view modal, and — project admins —
 * add/edit (B3b). The legacy page also merged per-customer custom prices from
 * `GET …/customusagefees`, a route the backend never registered (port plan
 * §1.3) — so the price shown is the fee's own price, as it effectively was.
 */
export function UsageFeeListPage() {
  const { projectId, isAdmin } = useProject()
  const { usagefeeId } = useParams<{ usagefeeId?: string }>()
  const navigate = useNavigate()
  const list = useApiQuery(() => usagefees.search(projectId), [projectId])
  const planList = useApiQuery(() => plansApi.search(projectId), [projectId])
  const [editing, setEditing] = useState<UsageFee | 'new' | null>(null)
  const base = `/projects/${encodeURIComponent(projectId)}/usagefees`

  const columns: Column<UsageFee>[] = [
    { key: 'name', label: 'Usage Fee', format: (_v, f) => <Link to={`${base}/${encodeURIComponent(f.id)}`}>{f.name}</Link> },
    { key: 'price', label: 'Price / unit', numeric: true, align: 'right', format: (_v, f) => usageFeePriceText(f) },
    {
      key: 'actions',
      label: '',
      sortable: false,
      format: (_v, f) =>
        isAdmin ? (
          <button type="button" onClick={() => setEditing(f)}>
            Edit
          </button>
        ) : null,
    },
  ]
  const selected = usagefeeId ? (list.data?.find((f) => f.id === usagefeeId) ?? null) : null

  return (
    <div className="portal-card">
      <PageTitle title="Usage Fees">
        {isAdmin && (
          <button type="button" onClick={() => setEditing('new')}>
            Add usage fee
          </button>
        )}
      </PageTitle>
      <QueryState loading={list.loading} error={list.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={list.data ?? []} rowKey={(f) => f.id} />
        </div>
        {list.data?.length === 0 && <p className="portal-text-muted">No usage fees yet.</p>}
      </QueryState>
      {selected && (
        <Modal open title={selected.name} onClose={() => navigate(base)}>
          <UsageFeeCalculator fee={selected} />
        </Modal>
      )}
      {editing && (
        <UsageFeeEditor
          projectId={projectId}
          fee={editing === 'new' ? null : editing}
          plans={planList.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.refetch()
          }}
        />
      )}
    </div>
  )
}

/** Legacy "Calculator" card — an estimate for the viewer, nothing is sent. */
function UsageFeeCalculator({ fee }: { fee: UsageFee }) {
  const [used, setUsed] = useState('0')
  const units = Number(used) || 0
  const unit = Number(fee.unit) || 1
  const blocks = Math.ceil(units / unit)
  const cost = fee.fixUnits ? blocks * Number(fee.price) : (units / unit) * Number(fee.price)
  return (
    <div className="fs-panel">
      <h3>Calculator</h3>
      <dl className="portal-kv">
        <dt>Price</dt>
        <dd>{usageFeePriceText(fee)}</dd>
        <dt>
          <label htmlFor="uf-used">{fee.unitName}s used:</label>
        </dt>
        <dd>
          <input id="uf-used" inputMode="decimal" value={used} onChange={(e) => setUsed(e.target.value)} />
        </dd>
        <dt>Blocks used:</dt>
        <dd className="fs-num">{blocks}</dd>
        <dt>Cost:</dt>
        <dd className="fs-num">
          {fee.currency.toUpperCase()} {formatNumberN(cost, 2)}
        </dd>
      </dl>
    </div>
  )
}

function UsageFeeEditor({
  projectId,
  fee,
  plans,
  onClose,
  onSaved,
}: {
  projectId: string
  fee: UsageFee | null
  plans: Plan[]
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !fee
  const [name, setName] = useState(fee?.name ?? '')
  const [price, setPrice] = useState(fee ? String(fee.price) : '')
  const [currency, setCurrency] = useState(fee?.currency ?? 'usd')
  const [unit, setUnit] = useState(fee ? String(fee.unit) : '1')
  const [unitName, setUnitName] = useState(fee?.unitName ?? '')
  const [fixUnits, setFixUnits] = useState(fee?.fixUnits ? 1 : 0)
  const [consumable, setConsumable] = useState(String(fee?.consumablePercent ?? 100))
  const [consumablePlans, setConsumablePlans] = useState(
    (fee?.consumablePlans ?? []).map((c) => ({ planId: c.planId, consumablePercent: String(c.consumablePercent) })),
  )
  const actions = useMoneyAction()

  const save = async () => {
    if (!name.trim()) return actions.setFeedback('save', { tone: 'warning', text: 'Enter a name.' })
    if (!DECIMAL.test(price.trim())) return actions.setFeedback('save', { tone: 'warning', text: 'Enter a valid price.' })
    if (!DECIMAL.test(unit.trim())) return actions.setFeedback('save', { tone: 'warning', text: 'Enter a valid unit (block size).' })
    const body: Record<string, unknown> = {
      name: name.trim(),
      price: price.trim(),
      currency,
      unit: unit.trim(),
      unitName: unitName.trim(),
      fixUnits,
      consumablePercent: consumable.trim(),
    }
    if (!isNew) body.consumablePlans = consumablePlans.filter((c) => c.planId)
    const result = await actions.run('save', JSON.stringify(body), () =>
      isNew ? usagefees.add(projectId, body) : usagefees.modify(projectId, fee!.id, body),
    )
    if (result) onSaved()
  }

  const title = isNew ? 'Add usage fee' : 'Edit usage fee'
  return (
    <Modal open title={title} onClose={onClose} className="portal-modal-wide">
      <form
        className="portal-form"
        aria-label={title}
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Price
          <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
        <label>
          Currency
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c.toUpperCase()}
              </option>
            ))}
          </select>
        </label>
        <label>
          Unit (block size)
          <input inputMode="decimal" value={unit} onChange={(e) => setUnit(e.target.value)} />
        </label>
        <label>
          Unit name
          <input value={unitName} onChange={(e) => setUnitName(e.target.value)} />
        </label>
        <label>
          Pay after every started unit
          <select value={fixUnits} onChange={(e) => setFixUnits(Number(e.target.value))}>
            <option value={0}>No (calculate fee exactly for 1 item precision)</option>
            <option value={1}>Yes (pay every started block)</option>
          </select>
        </label>
        <label>
          Consumable percent of usage fee
          <span className="portal-input-group">
            <input
              type="number"
              min={0}
              max={100}
              value={consumable}
              onChange={(e) => setConsumable(e.target.value)}
              aria-label="Consumable percent"
            />
            <input
              type="range"
              min={0}
              max={100}
              value={Number(consumable) || 0}
              onChange={(e) => setConsumable(e.target.value)}
              aria-label="Consumable percent slider"
            />
            <span className="portal-input-unit">%</span>
          </span>
        </label>
        {!isNew && (
          <fieldset className="fs-fieldset">
            <legend>Consumable plans</legend>
            {consumablePlans.map((c, i) => (
              <div key={i} className="portal-input-group">
                <select
                  aria-label={`Consumable plan ${i + 1}`}
                  value={c.planId}
                  onChange={(e) => {
                    const next = [...consumablePlans]
                    next[i] = { ...c, planId: e.target.value }
                    setConsumablePlans(next)
                  }}
                >
                  <option value="">— plan —</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={0}
                  max={100}
                  aria-label={`Consumable percent of plan ${i + 1}`}
                  value={c.consumablePercent}
                  onChange={(e) => {
                    const next = [...consumablePlans]
                    next[i] = { ...c, consumablePercent: e.target.value }
                    setConsumablePlans(next)
                  }}
                />
                <span className="portal-input-unit">%</span>
                <button
                  type="button"
                  aria-label={`Remove consumable plan ${i + 1}`}
                  onClick={() => setConsumablePlans(consumablePlans.filter((_, j) => j !== i))}
                >
                  −
                </button>
              </div>
            ))}
            <button type="button" onClick={() => setConsumablePlans([...consumablePlans, { planId: '', consumablePercent: '0' }])}>
              add
            </button>
          </fieldset>
        )}
        <div className="portal-inline-actions">
          <button type="submit" disabled={actions.isBusy('save')}>
            {actions.isBusy('save') ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
        <Notice feedback={actions.feedbackFor('save')} />
      </form>
    </Modal>
  )
}
